import { useEffect, useRef, useState, type JSX } from 'react';
import type { RecordingResult } from '../../../output/api';
import { Dialog } from '../../components/Dialog';
import { useRuntime, useUiContext } from '../../context';
import { formatBytes, formatDateTime, formatDuration } from '../../format';
import { safely } from '../../safe';

export function RecordingsDialog({ onClose }: { onClose(): void }): JSX.Element {
  const { studio, ui } = useUiContext();
  const status = useRuntime((r) => r.recording.status);
  const [list, setList] = useState<RecordingResult[] | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ id: string; url: string; audioOnly: boolean } | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => void (mounted.current = false);
  }, []);

  // The object URL pins the whole file in memory until it is revoked.
  useEffect(() => {
    const url = playing?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [playing?.url]);

  const play = (rec: RecordingResult): void => {
    if (playing?.id === rec.id) {
      setPlaying(null);
      return;
    }
    setBusy(rec.id);
    void safely(
      studio,
      async () => {
        const blob = await studio.getRecordingBlob(rec.id);
        if (!blob) throw new Error('The recording file is no longer available.');
        // Closed while the file was being read: a URL made now would never be revoked.
        if (!mounted.current) return;
        setPlaying({ id: rec.id, url: URL.createObjectURL(blob), audioOnly: rec.mimeType.startsWith('audio/') });
      },
      'Could not open the recording',
    ).finally(() => setBusy(null));
  };

  useEffect(() => {
    let cancelled = false;
    void safely(studio, () => studio.listRecordings(), 'Could not list recordings').then((result) => {
      if (!cancelled) setList(result ? [...result].sort((a, b) => b.createdAt - a.createdAt) : []);
    });
    return () => {
      cancelled = true;
    };
    // A finished recording changes the status back to idle, which is when the list grows.
  }, [studio, status, nonce]);

  const run = (id: string, work: () => Promise<void>, prefix: string): void => {
    if (playing?.id === id) setPlaying(null);
    setBusy(id);
    void safely(studio, work, prefix).finally(() => {
      setBusy(null);
      setNonce((n) => n + 1);
    });
  };

  return (
    <Dialog
      title="Recordings"
      onClose={onClose}
      width={980}
      height={480}
      footer={
        <button type="button" className="ui-btn primary" onClick={onClose}>
          Close
        </button>
      }
    >
      {playing ? (
        <video
          key={playing.url}
          src={playing.url}
          controls
          autoPlay
          aria-label="Recording playback"
          style={{ width: '100%', maxHeight: playing.audioOnly ? 54 : 260, background: '#000', marginBottom: 10 }}
        />
      ) : null}
      {list === null ? <div className="ui-empty">Loading…</div> : null}
      {list?.length === 0 ? <div className="ui-empty">No recordings yet. Finished recordings are kept in this browser until you delete them.</div> : null}
      {list && list.length > 0 ? (
        <table className="ui-table">
          <thead>
            <tr>
              <th scope="col">File</th>
              <th scope="col">Recorded</th>
              <th scope="col">Length</th>
              <th scope="col">Size</th>
              <th scope="col">Stored</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {list.map((rec) => (
              <tr key={rec.id}>
                <th scope="row" className="ui-ellipsis ui-selectable" style={{ maxWidth: 220 }} title={rec.mimeType}>
                  {rec.fileName}
                </th>
                <td>{formatDateTime(rec.createdAt)}</td>
                <td className="ui-mono">{formatDuration(rec.durationMs)}</td>
                <td className="ui-mono">{formatBytes(rec.sizeBytes)}</td>
                <td>{rec.sink === 'opfs' ? 'Browser storage' : 'Memory (this session)'}</td>
                <td>
                  <div className="ui-row" style={{ justifyContent: 'flex-end' }}>
                    <button type="button" className="ui-btn" disabled={busy === rec.id} aria-pressed={playing?.id === rec.id} onClick={() => play(rec)}>
                      {playing?.id === rec.id ? 'Close player' : 'Play'}
                    </button>
                    <button
                      type="button"
                      className="ui-btn"
                      disabled={busy === rec.id}
                      onClick={() => run(rec.id, () => studio.downloadRecording(rec.id), 'Download failed')}
                    >
                      Download
                    </button>
                    <button
                      type="button"
                      className="ui-btn"
                      disabled={busy === rec.id}
                      onClick={() =>
                        ui.getState().openDialog({
                          type: 'confirm',
                          title: 'Delete recording',
                          message: `Delete "${rec.fileName}" permanently?`,
                          confirmLabel: 'Delete',
                          danger: true,
                          onConfirm: () => run(rec.id, () => studio.deleteRecording(rec.id), 'Delete failed'),
                        })
                      }
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Dialog>
  );
}
